import { createSlice } from '@reduxjs/toolkit';
import statusConstants from 'store/reducers/common/constants';
import {
  bumpAiPollGeneration,
} from 'v2/apps/clink/pages/boq/boqAiPollGeneration';
import {
  REFINE_BOQ_STATE_CODE,
  bumpRefinePollGeneration,
} from 'v2/apps/clink/pages/boq/boqRefinement';
import extraReducers, {
  fetchBoQList,
  fetchBoQByTenderId,
  fetchBoQQuotes,
  fetchUnits,
  fetchProjectStatuses,
  createEntity,
  updateEntity,
  generateBoqWithAI,
  fetchGenerateBoqStatus,
  publishBoQ,
  republishBoQ,
  quoteItems,
  quoteItemsDocs,
  publishQuote,
  republishQuote,
  fetchOrderTemplates,
  changeCompliance,
  fetchQuoteHistory,
} from './extraReducers';

/**
 * Rows leave the review one at a time, so the outcome is tallied as it happens —
 * the review is gone by the time the summary renders.
 */
const tallyResolvedRefineRows = (
  state,
  packageId,
  { applied = 0, discarded = 0 }
) => {
  const job = state.refineById?.[packageId];
  if (!job) return;
  job.appliedCount = (job.appliedCount || 0) + applied;
  job.discardedCount = (job.discardedCount || 0) + discarded;
};

const completeRefineReview = (state, packageId) => {
  delete state.refineReviewById[packageId];
  if (state.refineById?.[packageId]) {
    state.refineById[packageId].stateCode =
      REFINE_BOQ_STATE_CODE.REVIEW_COMPLETE;
  }
};

const initialState = {
  units: [],
  entity: null,
  entities: [],
  uiLoading: {
    boqList: false,
    /** PATCH `boq/entity/:id` in-flight by entity id */
    updateEntityById: {},
    /** "New BoQ" destructive reset in-flight by entity id */
    newBoqResetById: {},
    /** publish/republish in-flight by entity id */
    publishById: {},
    /** GET `ai/generate-boq/:id` poll in-flight by entity id */
    aiPollById: {},
    /** GET `ai/refine-boq/:id` poll in-flight by entity id */
    refinePollById: {},
  },
  aiGenerationById: {},
  /** Incremented on new AI POST / clear; stale GET responses are ignored. */
  aiPollGenerationById: {},
  /** After "Retry analysis", skip automatic GET ai/generate-boq rehydration until user starts AI again */
  aiRehydrationSkippedById: {},
  /** GET ai/generate-boq failed for this BoQ id; snackbar when tab is active (enable), cleared on success / Retry / new AI */
  aiGetPollStickyById: {},
  /** Session-only refinement lifecycle/result keyed by package id. */
  refineById: {},
  /** Pending update-only cell reviews keyed by package id. */
  refineReviewById: {},
  /** Incremented on each refinement POST/clear; stale polls are ignored. */
  refinePollGenerationById: {},
  editing: false,
  published: false,
  loadedQuotes: false,
  quotes: [],
  projectStatuses: null,
  note: {},
  error: null,
  orderTemplates: [],
  loading: {
    severity: '',
    message: 'Loading',
    type: statusConstants.IDLE_STATUS,
  },
  editingQuote: false,
  nextEntries: [],
  readyAfterTemplate: false,
  quoteHistory: [],
};

const boqSlice = createSlice({
  name: 'boq',
  initialState,
  reducers: {
    setSelectedEntries(state, action) {
      const { payload } = action;
      const { id, rows } = payload;
      if (id) {
        state.entities = [...state.entities].map((entity) =>
          Number(entity.id) === Number(id)
            ? { ...entity, nextEntries: rows }
            : entity
        );
      } else {
        state.entity = { ...state.entity, nextEntries: rows };
      }
    },
    setEditingMode(state, action) {
      const { payload } = action;
      const { id, value, excel } = payload;
      state.entities = [...state.entities].map((entity) =>
        Number(entity.id) === Number(id)
          ? { ...entity, editing: value }
          : entity
      );
      state.readyAfterTemplate = excel;
    },
    setEditingQuoteMode(state, action) {
      const { payload } = action;
      const { value } = payload;
      state.editingQuote = value;
    },
    setEntitySaved(state, action) {
      const { payload } = action;
      const { eid, value } = payload;
      state.entities = state.entities.map((e) =>
        e.id === eid
          ? {
              ...e,
              saved: value,
            }
          : e
      );
    },
    setPackageNote(state, action) {
      const { payload } = action;
      const { newNote, eid } = payload;
      state.entities = state.entities.map((e) => {
        if (e.id === eid) {
          return {
            ...e,
            nextNote: newNote,
          };
        }
        return e;
      });
    },
    setSubmitQuoteVars(state, action) {
      const { payload } = action;
      const { key, value } = payload;
      if (state.entity && [key] in state.entity) {
        state.entity[key] = value;
      }
    },
    setLocalLoadQuotes(state, action) {
      state.loadedQuotes = action?.payload ?? false;
    },
    clearAiGenerationResult(state, action) {
      const packageId = action?.payload?.packageId;
      if (!packageId || !state.aiGenerationById?.[packageId]) return;
      state.aiGenerationById = {
        ...state.aiGenerationById,
        [packageId]: {
          ...state.aiGenerationById[packageId],
          result: null,
        },
      };
    },
    clearAiGenerationForPackage(state, action) {
      const packageId = action?.payload?.packageId;
      if (!packageId) return;
      bumpAiPollGeneration(state, packageId);
      if (state.aiGenerationById?.[packageId]) {
        const next = { ...state.aiGenerationById };
        delete next[packageId];
        state.aiGenerationById = next;
      }
      state.aiRehydrationSkippedById = {
        ...(state.aiRehydrationSkippedById || {}),
        [packageId]: true,
      };
      if (state.aiGetPollStickyById?.[packageId]) {
        const nextSticky = { ...state.aiGetPollStickyById };
        delete nextSticky[packageId];
        state.aiGetPollStickyById = nextSticky;
      }
    },
    acceptRefineRow(state, action) {
      const { packageId, rowId, position } = action.payload || {};
      const review = state.refineReviewById?.[packageId];
      if (!review) return;
      const rowReview = review.rows.find(
        (row) =>
          (rowId != null && row.rowId === rowId) ||
          (rowId == null && Number(row.position) === Number(position))
      );
      if (!rowReview) return;

      state.entities = state.entities.map((entity) => {
        if (Number(entity.id) !== Number(packageId)) return entity;
        const currentRows = Array.isArray(entity.nextEntries)
          ? entity.nextEntries
          : entity.entries;
        return {
          ...entity,
          nextEntries: currentRows.map((row) => {
            const matches =
              (rowReview.rowId != null && row.id === rowReview.rowId) ||
              (rowReview.rowId == null &&
                Number(row.position) === Number(rowReview.position));
            if (!matches) return row;
            return rowReview.changedCells.reduce(
              (nextRow, cell) => ({
                ...nextRow,
                [cell.field]: cell.after,
              }),
              row
            );
          }),
        };
      });

      if (Number(state.entity?.id) === Number(packageId)) {
        const currentRows = Array.isArray(state.entity.nextEntries)
          ? state.entity.nextEntries
          : state.entity.entries;
        state.entity.nextEntries = currentRows.map((row) => {
          const matches =
            (rowReview.rowId != null && row.id === rowReview.rowId) ||
            (rowReview.rowId == null &&
              Number(row.position) === Number(rowReview.position));
          if (!matches) return row;
          return rowReview.changedCells.reduce(
            (nextRow, cell) => ({
              ...nextRow,
              [cell.field]: cell.after,
            }),
            row
          );
        });
      }

      review.rows = review.rows.filter((row) => row !== rowReview);
      tallyResolvedRefineRows(state, packageId, { applied: 1 });
      if (!review.rows.length) {
        completeRefineReview(state, packageId);
      }
    },
    rejectRefineRow(state, action) {
      const { packageId, rowId, position } = action.payload || {};
      const review = state.refineReviewById?.[packageId];
      if (!review) return;
      const remaining = review.rows.filter(
        (row) =>
          !(
            (rowId != null && row.rowId === rowId) ||
            (rowId == null && Number(row.position) === Number(position))
          )
      );
      if (remaining.length === review.rows.length) return;
      review.rows = remaining;
      tallyResolvedRefineRows(state, packageId, { discarded: 1 });
      if (!review.rows.length) {
        completeRefineReview(state, packageId);
      }
    },
    acceptAllRefineRows(state, action) {
      const packageId = action.payload?.packageId;
      const review = state.refineReviewById?.[packageId];
      if (!review) return;
      const changesByRow = new Map(
        review.rows.map((row) => [
          row.rowId != null ? `id:${row.rowId}` : `position:${row.position}`,
          row,
        ])
      );
      const applyRows = (rows) =>
        (rows || []).map((row) => {
          const rowReview =
            changesByRow.get(`id:${row.id}`) ||
            changesByRow.get(`position:${row.position}`);
          if (!rowReview) return row;
          return rowReview.changedCells.reduce(
            (nextRow, cell) => ({ ...nextRow, [cell.field]: cell.after }),
            row
          );
        });

      state.entities = state.entities.map((entity) =>
        Number(entity.id) === Number(packageId)
          ? {
              ...entity,
              nextEntries: applyRows(
                Array.isArray(entity.nextEntries)
                  ? entity.nextEntries
                  : entity.entries
              ),
            }
          : entity
      );
      if (Number(state.entity?.id) === Number(packageId)) {
        state.entity.nextEntries = applyRows(
          Array.isArray(state.entity.nextEntries)
            ? state.entity.nextEntries
            : state.entity.entries
        );
      }
      tallyResolvedRefineRows(state, packageId, {
        applied: review.rows.length,
      });
      completeRefineReview(state, packageId);
    },
    rejectAllRefineRows(state, action) {
      const packageId = action.payload?.packageId;
      const review = state.refineReviewById?.[packageId];
      if (!review) return;
      tallyResolvedRefineRows(state, packageId, {
        discarded: review.rows.length,
      });
      completeRefineReview(state, packageId);
    },
    clearRefineBoqForPackage(state, action) {
      const packageId = action.payload?.packageId;
      if (packageId == null) return;
      bumpRefinePollGeneration(state, packageId);
      delete state.refineById[packageId];
      delete state.refineReviewById[packageId];
      if (state.uiLoading?.refinePollById?.[packageId]) {
        delete state.uiLoading.refinePollById[packageId];
      }
    },
  },
  extraReducers,
});

export {
  fetchBoQList,
  fetchBoQByTenderId,
  fetchBoQQuotes,
  fetchUnits,
  fetchProjectStatuses,
  createEntity,
  updateEntity,
  generateBoqWithAI,
  fetchGenerateBoqStatus,
  publishBoQ,
  republishBoQ,
  quoteItems,
  quoteItemsDocs,
  publishQuote,
  republishQuote,
  fetchOrderTemplates,
  changeCompliance,
  fetchQuoteHistory,
};
export { refineBoqWithAI, fetchRefineBoqStatus } from './extraReducers';
export const {
  setSelectedEntries,
  setPackageNote,
  setEditingMode,
  setEntitySaved,
  setSubmitQuoteVars,
  setEditingQuoteMode,
  setLocalLoadQuotes,
  clearAiGenerationResult,
  clearAiGenerationForPackage,
  acceptRefineRow,
  rejectRefineRow,
  acceptAllRefineRows,
  rejectAllRefineRows,
  clearRefineBoqForPackage,
} = boqSlice.actions;
export default boqSlice.reducer;
